import { useState } from "react";
import { Download, Share2, FileText } from "lucide-react";
import { useLang } from "@/i18n";
import { SITE } from "@/config/site";
import { PageHero, Reveal } from "@/components/common";
import { toast } from "sonner";

const CHAPTERS = [
  { id: "executive-summary", en: "Executive Summary", pt: "Sumário Executivo",
    body: {
      en: "MAGABRAZIL ($MBGA) is a Brazilian-born memecoin concept on Solana, combining meme culture, digital collectibles and a transparent community ecosystem. This document presents the vision, technology and architecture behind the project. $MBGA is a speculative digital asset: nothing here constitutes investment advice or a promise of returns.",
      pt: "A MAGABRAZIL ($MBGA) é um conceito de memecoin nascido no Brasil na rede Solana, combinando cultura meme, colecionáveis digitais e um ecossistema comunitário transparente. Este documento apresenta a visão, a tecnologia e a arquitetura do projeto. $MBGA é um ativo digital especulativo: nada aqui constitui recomendação de investimento ou promessa de retorno.",
    } },
  { id: "vision-identity", en: "Vision & Identity", pt: "Visão e Identidade",
    body: {
      en: "A brand inspired by Brazilian culture and digital creativity, built for a global community. The green, gold and dark cinematic identity expresses monumentality, pride and a premium Web3 aesthetic.",
      pt: "Uma marca inspirada na cultura brasileira e na criatividade digital, construída para uma comunidade global. A identidade cinematográfica em verde, ouro e tons escuros expressa monumentalidade, orgulho e uma estética Web3 premium.",
    } },
  { id: "solana-technology", en: "Solana Technology", pt: "Tecnologia Solana",
    body: {
      en: "Solana offers high throughput, low fees and fast finality — the infrastructure chosen for the MBGA token, the Genesis NFT collection and future applications.",
      pt: "A Solana oferece alta capacidade, taxas baixas e finalidade rápida — a infraestrutura escolhida para o token MBGA, a coleção NFT Genesis e futuras aplicações.",
    } },
  { id: "token-architecture", en: "Token Architecture", pt: "Arquitetura do Token",
    body: {
      en: "MBGA is planned as an SPL token with a fixed supply of 900,000,000 units. The official mint address will be published only after deployment and verification. Until then, no contract address exists.",
      pt: "O MBGA é planejado como um token SPL com supply fixo de 900.000.000 unidades. O endereço oficial do mint será publicado somente após implantação e verificação. Até lá, nenhum endereço de contrato existe.",
    } },
  { id: "tokenomics", en: "Tokenomics", pt: "Tokenomics",
    body: {
      en: "Planned allocation: 40% liquidity & exchanges, 20% social fund & utilities, 15% marketing & creators, 10% team & development, 10% ecosystem rewards, 5% partnerships. Final parameters replace this proposal after on-chain verification.",
      pt: "Alocação planejada: 40% liquidez e exchanges, 20% fundo social e utilidades, 15% marketing e criadores, 10% equipe e desenvolvimento, 10% recompensas do ecossistema, 5% parcerias. Os parâmetros finais substituem esta proposta após verificação on-chain.",
    } },
  { id: "bonding-liquidity", en: "Bonding Curve & Liquidity", pt: "Bonding Curve e Liquidez",
    body: {
      en: "The reference scenario is a direct CPMM pool. If creation occurs via Raydium LaunchLab, its final parameters will be published and will supersede this proposal.",
      pt: "O cenário de referência é um pool CPMM direto. Caso a criação ocorra via Raydium LaunchLab, seus parâmetros finais serão publicados e substituirão esta proposta.",
    } },
  { id: "security-authorities", en: "Security & Authorities", pt: "Segurança e Autoridades",
    body: {
      en: "Mint and freeze authority statuses will be verifiable on-chain after deployment. Security reviews precede any launch. No private keys are ever held by this website.",
      pt: "Os status das autoridades de mint e freeze serão verificáveis on-chain após a implantação. Revisões de segurança precedem qualquer lançamento. Este site jamais custodia chaves privadas.",
    } },
  { id: "vesting-treasury", en: "Vesting & Treasury", pt: "Vesting e Tesouraria",
    body: {
      en: "Team proposal: 90,000,000 MBGA with a 6-month cliff and 30 months of linear unlock (36 months total). The treasury is planned as a multisig with publicly verifiable addresses.",
      pt: "Proposta da equipe: 90.000.000 MBGA com cliff de 6 meses e desbloqueio linear de 30 meses (36 meses no total). A tesouraria é planejada como multisig com endereços publicamente verificáveis.",
    } },
  { id: "nft-membership", en: "NFT Membership", pt: "Membresia NFT",
    body: {
      en: "The Genesis collection connects collectibles to community participation. Proposed eligibility: holding the equivalent of at least US$ 1,000 in MBGA for 30 consecutive days, validated by trusted price data and on-chain history. NFT ownership generates no dividends and guarantees no economic value.",
      pt: "A coleção Genesis conecta colecionáveis à participação na comunidade. Elegibilidade proposta: manter o equivalente a pelo menos US$ 1.000 em MBGA por 30 dias consecutivos, validado por dados confiáveis de preço e histórico on-chain. A posse do NFT não gera dividendos nem garante valor econômico.",
    } },
  { id: "mbga-application", en: "MBGA Application", pt: "Aplicativo MBGA",
    body: {
      en: "A planned web app with wallet connect, balances, charts, NFTs, rewards and treasury transparency. Status: in development.",
      pt: "Um aplicativo web planejado com conexão de carteira, saldos, gráficos, NFTs, recompensas e transparência da tesouraria. Status: em desenvolvimento.",
    } },
  { id: "future-card", en: "Future Card", pt: "Cartão Futuro",
    body: {
      en: "The MBGA Card is a concept: partner benefits, member digital identity and a future payment card conditioned on an authorized issuing partner and regulatory requirements. It is not a financial product currently issued.",
      pt: "O MBGA Card é um conceito: benefícios com parceiros, identidade digital de membro e um futuro cartão de pagamento condicionado a emissor autorizado e exigências regulatórias. Não é um produto financeiro atualmente emitido.",
    } },
  { id: "community", en: "Community", pt: "Comunidade",
    body: {
      en: "An open, global community gathered around Brazilian meme culture. Official channels are published only after verification.",
      pt: "Uma comunidade global e aberta em torno da cultura meme brasileira. Os canais oficiais são publicados somente após verificação.",
    } },
  { id: "roadmap", en: "Roadmap", pt: "Roadmap",
    body: {
      en: "Six phases from foundation (Q4 2026) to expansion (2028), each with a public, editable status. No phase is marked complete without administrative confirmation and evidence.",
      pt: "Seis fases, da fundação (Q4 2026) à expansão (2028), cada uma com status público e editável. Nenhuma fase é marcada como concluída sem confirmação administrativa e evidência.",
    } },
  { id: "governance", en: "Governance", pt: "Governança",
    body: {
      en: "Community votes may be consultative. No voting mechanism grants power over funds or contracts without effectively implemented mechanisms.",
      pt: "Votações comunitárias poderão ser consultivas. Nenhum mecanismo de voto atribui poder sobre fundos ou contratos sem mecanismos efetivamente implementados.",
    } },
  { id: "legal-compliance", en: "Legal & Compliance", pt: "Jurídico e Conformidade",
    body: {
      en: "Preliminary legal documentation is published for transparency and will undergo professional review. MAGABRAZIL does not claim affiliation with or endorsement by political figures or institutions.",
      pt: "A documentação jurídica preliminar é publicada por transparência e passará por revisão profissional. A MAGABRAZIL não declara afiliação ou endosso de figuras políticas ou instituições.",
    } },
  { id: "risk-disclosure", en: "Risk Disclosure", pt: "Aviso de Riscos",
    body: {
      en: "Digital assets are volatile and may lose all value. Risks include liquidity, smart contract failures, scams, custody and regulatory changes. Never invest more than you can afford to lose.",
      pt: "Ativos digitais são voláteis e podem perder todo o valor. Os riscos incluem liquidez, falhas em contratos inteligentes, golpes, custódia e mudanças regulatórias. Nunca invista mais do que pode perder.",
    } },
  { id: "technical-references", en: "Technical References", pt: "Referências Técnicas",
    body: {
      en: "Solana documentation, SPL Token program, Metaplex Core, Jupiter and DexScreener APIs, and on-chain indexers are the technical references for future integrations.",
      pt: "Documentação da Solana, programa SPL Token, Metaplex Core, APIs da Jupiter e DexScreener e indexadores on-chain são as referências técnicas para integrações futuras.",
    } },
  { id: "official-contracts", en: "Official Contracts & Links", pt: "Contratos e Links Oficiais",
    body: {
      en: "No official contract exists yet. Every address will be published here and on the Transparency page after deployment and verification.",
      pt: "Ainda não existe contrato oficial. Cada endereço será publicado aqui e na página de Transparência após implantação e verificação.",
    } },
];

export default function Whitepaper() {
  const { t, lang } = useLang();
  const [activeId, setActiveId] = useState(CHAPTERS[0].id);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success(t("wp.copied"));
    } catch { /* noop */ }
  };

  return (
    <>
      <PageHero eyebrow={t("wp.eyebrow")} title={t("wp.title")} sub={t("wp.sub")} />

      <section className="section-pad" data-testid="whitepaper-page">
        <Reveal className="card-surface flex flex-wrap items-center gap-x-8 gap-y-3 p-6">
          <span className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-mist">
            <FileText size={14} className="text-goldlight" /> {t("wp.version")}: <b className="text-paper">{SITE.whitepaperVersion}</b>
          </span>
          <span className="font-mono text-xs uppercase tracking-widest text-mist">
            {t("wp.status")}: <b className="text-goldlight">{t("wp.statusVal")}</b>
          </span>
          <div className="ml-auto flex gap-3">
            {SITE.whitepaperPdfUrl ? (
              <a href={SITE.whitepaperPdfUrl} className="btn-neon !px-5 !py-2 !text-xs" data-testid="wp-download">
                <Download size={13} /> {t("wp.download")}
              </a>
            ) : (
              <span className="mono-chip border-gold/40 text-goldlight" data-testid="wp-pdf-pending">
                <Download size={12} /> {t("wp.pdfPending")}
              </span>
            )}
            <button type="button" onClick={share} className="btn-ghost !px-5 !py-2 !text-xs" data-testid="wp-share">
              <Share2 size={13} /> {t("wp.share")}
            </button>
          </div>
        </Reveal>

        <div className="mt-8 grid gap-10 lg:grid-cols-[260px_1fr]">
          <aside className="lg:sticky lg:top-24 lg:self-start" data-testid="wp-index">
            <p className="font-heading text-[11px] font-bold uppercase tracking-[0.25em] text-goldlight">{t("wp.index")}</p>
            <nav className="mt-4 flex max-h-[60vh] flex-col gap-1 overflow-y-auto pr-2">
              {CHAPTERS.map((c, i) => (
                <a
                  key={c.id}
                  href={`#${c.id}`}
                  onClick={() => setActiveId(c.id)}
                  data-testid={`wp-toc-${c.id}`}
                  className={`rounded-lg px-3 py-1.5 text-xs transition-colors ${
                    activeId === c.id ? "bg-gold/10 text-goldlight" : "text-mist hover:text-paper"
                  }`}
                >
                  {String(i + 1).padStart(2, "0")} · {c[lang]}
                </a>
              ))}
            </nav>
          </aside>

          <div className="max-w-3xl space-y-12 pb-10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.id}>
                <article id={c.id} className="scroll-mt-28" data-testid={`wp-chapter-${c.id}`}>
                  <h2 className="flex items-baseline gap-3 font-heading text-xl font-bold uppercase tracking-wide text-paper">
                    <span className="font-mono text-sm text-gold">{String(i + 1).padStart(2, "0")}</span>
                    {c[lang]}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-mist">{c.body[lang]}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
